// Display helpers for the backend's product-neutral ullage fields
// (ullage_height in inches, ullage_volume in US gallons). Values are always
// taken from the payload as published; the frontend never computes geometry.

export const ULLAGE_ESTIMATE_TOOLTIP =
  "Estimated empty tank volume; not a safe delivery quantity.";

export const ULLAGE_UNAVAILABLE_TEXT = "--";

const REASON_TEXT = {
  missing_dimensions: "Tank dimensions are not configured.",
  unsupported_shape: "This tank shape has no validated volume model.",
  invalid_dimensions: "Tank dimensions are invalid or inconsistent.",
  invalid_reading: "The latest sensor reading is an error or out of range.",
  invalid_liquid_height: "The latest liquid height is outside the tank's physical range.",
  invalid_calibration: "Sensor calibration is incomplete.",
};

const numberFormat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// A valid zero is a real value; only null/missing/non-valid shows "--".
export function getUllageValue(data, key) {
  if (!data || data.ullage_status !== "valid") return null;
  const value = data[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function formatUllageNumber(value) {
  return value === null || value === undefined
    ? ULLAGE_UNAVAILABLE_TEXT
    : numberFormat.format(value);
}

export function formatUllageVolume(data) {
  return formatUllageNumber(getUllageValue(data, "ullage_volume"));
}

export function formatUllageHeight(data) {
  return formatUllageNumber(getUllageValue(data, "ullage_height"));
}

// Explanation for a "--" value; null when the value is valid.
export function getUllageStateText(data) {
  if (getUllageValue(data, "ullage_volume") !== null) return null;
  if (!data || data.ullage_status === undefined) {
    return "Ullage has not been calculated for this reading yet.";
  }
  return REASON_TEXT[data.ullage_reason] ?? "Ullage is unavailable for this reading.";
}

export function getUllageTooltip(data) {
  return getUllageStateText(data) ?? ULLAGE_ESTIMATE_TOOLTIP;
}

// Applies to liquid-level readings: either the backend published ullage
// fields, or the asset has a tank shape configured.
export function isUllageApplicable(data, tankShape) {
  return (
    (data && data.ullage_status !== undefined) ||
    (typeof tankShape === "string" && tankShape.trim() !== "")
  );
}
